import styled, { css } from "styled-components";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { fontSizes, fontWeights } from "../../../design-system/tokens/typography";

/** Height of a picker set inside a sentence. */
/* GAP(inlinePickHeight): 36px, between controlSize.field.sm (32) and md (40) */
export const INLINE_PICK_HEIGHT = "2.25rem";

interface InlinePickState {
  open?: boolean;
  invalid?: boolean;
}

/**
 * The look of a picker inside a sentence: 36px tall, 15px semibold, 1px strong border, r8,
 * white; open = 2px primary border with the padding taking back the extra pixel.
 * Shared by `InlinePickTrigger` and the `appearance="sentence"` of `DateField` and `NumberStepper`.
 */
export const inlinePickCss = ({ open = false, invalid = false }: InlinePickState = {}) => css`
  box-sizing: border-box;
  height: ${INLINE_PICK_HEIGHT};
  min-height: ${INLINE_PICK_HEIGHT};
  width: auto;
  padding: 0 ${open ? `calc(${spacing[3]} - 1px)` : spacing[3]};
  border: ${open ? 2 : 1}px solid
    ${open ? rawColors.primary[500] : invalid ? rawColors.accent.pink : rawColors.stroke.strong};
  border-radius: ${borderRadius.lg};
  background-color: ${rawColors.structural.white};
  color: ${rawColors.text.highEmphasis};
  font-size: ${fontSizes["15"]};
  font-weight: ${fontWeights.semibold};
  line-height: 1;
`;

export const Trigger = styled.button<{ $open: boolean; $invalid: boolean; $placeholder: boolean }>`
  ${({ $open, $invalid }) => inlinePickCss({ open: $open, invalid: $invalid })}
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: ${spacing[2]};
  max-width: 100%;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  ${({ $placeholder }) => $placeholder && `color: ${rawColors.text.lowEmphasis};`}

  &:focus-visible {
    outline: none;
    border-color: ${rawColors.primary[500]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const IconSlot = styled.span`
  display: inline-flex;
  flex: none;
  color: ${rawColors.text.mediumEmphasis};
  pointer-events: none;
`;
